import {
  Button,
  Col,
  Icon,
  Row,
  SafeHtmlContent,
  Text,
  Unifree,
  YesObject
} from '@yes.technology/react-toolkit'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

import { useUploadS3 } from 'modules/interaction/hooks'
import { SELECT_AND_LINK_ACTION } from 'modules/interaction/hooks/useActions'
import FileUploader from 'modules/shared/FileUploader'
import { useDocument } from 'modules/shared/hooks'
import { formatFileSize } from 'modules/shared/utils/formatFileSize'
import styled from 'styled-components'
import { ActivityModel } from 'types/shared'
import { useSelectAndLinkFrameContext } from '../../SelectAndLinkFrame/SelectAndLinkFrameContext'
import { ActivityInputProps } from '../Activity.types'
import ActivityWithActions from '../ActivityWithActions/ActivityWithActions'
import { UploadFileResult } from 'modules/interaction/hooks/useUploadS3/useUploadS3'
import { useAppDispatch } from 'state/store'
import { startLoading, stopLoading } from 'modules/interaction/redux'
import useBlueprintsContext from 'modules/interaction/hooks/useBlueprintsContext'

type handleFileUploadSuccessProps = UploadFileResult & {
  fileName: string
}

interface ActivityDocumentFileProps {
  uuid?: string
  inputValue: ActivityModel['value']
  onChange: ActivityInputProps['handleChange']
  label: string
  explanationText?: string
  required?: boolean
  actions?: string[]
  maxSizeInBytes?: number
  accept?: string
  hideDescription?: boolean
}

interface Values {
  uuid?: string
  uuid_status?: string
  statusDes?: string
  contentType?: string
  size?: string
  url?: string
  des?: string
  filename?: string
  originalFilename?: string
}

const ACCEPT_PLACEHOLDER_MAP: Record<string, string> = {
  'image/*': '...jpeg, jpg, etc.',
  'video/*': '...mp4, etc.',
  'audio/*': '...mp3, etc.',
  'application/*': '...pdf, doc, etc.',
  'image/jpeg': '.jpeg, .jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'video/mp4': '.mp4',
  'video/webm': '.webm',
  'audio/mpeg': '.mp3',
  'application/pdf': '.pdf'
}

const getContentTypePlaceholder = (accept?: string): string | undefined => {
  if (!accept) return undefined
  if (ACCEPT_PLACEHOLDER_MAP[accept]) return ACCEPT_PLACEHOLDER_MAP[accept]
  const subtype = accept.split('/')[1]
  if (subtype && subtype !== '*') return `.${subtype}`
  return accept
}

const TextAlignJustify = styled.span`
  text-align: justify;
`

const ActivityDocumentFile = ({
  uuid,
  inputValue,
  onChange,
  label,
  explanationText,
  required,
  actions = [],
  maxSizeInBytes,
  accept,
  hideDescription
}: ActivityDocumentFileProps) => {
  const { t } = useTranslation()
  const { createOrUpdateDocument } = useDocument()
  const { uploadFile } = useUploadS3()
  const { setSelectAndLinkData } = useSelectAndLinkFrameContext()

  const dispatch = useAppDispatch()
  const { executeBlueprints } = useBlueprintsContext()

  const [values, setValues] = useState<Values>()

  useEffect(() => {
    if (typeof inputValue === 'string' && inputValue) {
      setValues(JSON.parse(inputValue))
    }
  }, [inputValue])

  const startLoadingPage = () => {
    dispatch(
      startLoading({
        des: t('file-uploader.loading.des'),
        complement: t('file-uploader.loading.complement')
      })
    )
  }

  const stopLoadingPage = () => {
    dispatch(stopLoading())
  }

  const handleActionClick = (action: string, uniselection?: boolean) => {
    if (action === SELECT_AND_LINK_ACTION) {
      setSelectAndLinkData({
        uuidObjectclass: window.documentObjectclassUuid,
        callback: async (values) => {
          const object: YesObject = values?.[0]
          if (object) {
            const { statusDes, statusUuid } = await createOrUpdateDocument({
              uuid: object.uuid
            })

            if (statusDes) {
              const value = JSON.stringify({
                uuid: object.uuid,
                uuid_status: statusUuid,
                statusDes: statusDes,
                des: object.des,
                contentType: object.content_type
              })

              onChange(value)

              executeBlueprints({
                type: 'activity_changed',
                activityValue: value,
                activityModelUuid: uuid
              })
            }
          }
        },
        uniselection
      })
    }
  }

  const handleFileUploadSuccess = async ({
    url,
    content,
    fileName
  }: handleFileUploadSuccessProps) => {
    const { document, statusDes, statusUuid } = await createOrUpdateDocument({
      uuid: values?.uuid,
      content_type: content.type,
      url,
      complement: JSON.stringify({ size: content.size })
    })

    onChange(
      JSON.stringify({
        originalFilename: fileName,
        uuid: document?.uuid,
        uuid_status: statusUuid,
        statusDes: statusDes,
        des: document?.des,
        contentType: content.type,
        size: content.size,
        url,
        filename: content.name
      })
    )
    executeBlueprints({
      type: 'activity_changed',
      activityValue: '',
      activityModelUuid: uuid
    })
    stopLoadingPage()
  }

  const handleLocalFileUpload = (file: File) => {
    startLoadingPage()

    onChange(
      JSON.stringify({
        ...values,
        originalFilename: file.name
      })
    )

    uploadFile({
      file,
      fileType: file.type
    })
      .then((args: UploadFileResult) =>
        handleFileUploadSuccess({ ...args, fileName: file.name })
      )
      .catch(stopLoadingPage)
  }

  const handleClearData = () => {
    onChange('')
    setValues(undefined)
    executeBlueprints({ type: 'activity_changed' })
  }

  const handleDescriptionChange = (value: string) => {
    onChange(
      JSON.stringify({
        ...values,
        des: value
      })
    )
  }

  const handleSaveDescription = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!inputValue || !values?.url) {
      return
    }

    // workaround to solve a react issue when an event triggers a re-render the other events don't fire. As discussed this workaround should be deleted in a future refactor
    setTimeout(async () => {
      const parsedValues =
        typeof inputValue === 'string' && inputValue !== ''
          ? JSON.parse(inputValue)
          : inputValue
      const { document, statusDes, statusUuid } = await createOrUpdateDocument(
        {
          uuid: parsedValues.uuid,
          des: event.target.value
        },
        false
      )

      onChange(
        JSON.stringify({
          ...parsedValues,
          uuid: document?.uuid,
          des: event.target.value,
          uuid_status: statusUuid,
          statusDes: statusDes
        })
      )
    }, 500)
  }

  return (
    <>
      {label && (
        <Row>
          <Col lg={12}>
            <TextAlignJustify>
              <Text as='label' variant='label-03'>
                {label}
              </Text>
            </TextAlignJustify>
          </Col>
        </Row>
      )}

      {explanationText && (
        <Row className='mt-2'>
          <Col lg={12}>
            <TextAlignJustify>
              <Text as='p' variant='content-04'>
                <SafeHtmlContent html={explanationText} />
              </Text>
            </TextAlignJustify>
          </Col>
        </Row>
      )}

      {!hideDescription && (
        <Row className='mt-2'>
          <Col lg={12}>
            <Unifree
              label={t('activity-document-file.labels.des')}
              value={values?.des || ''}
              onChange={handleDescriptionChange}
              onBlur={handleSaveDescription}
              data-testid='description-field'
              disabled={!values?.url}
              required={required}
              placeholder={t('activity-document-file.placeholders.des')}
            />
          </Col>
        </Row>
      )}

      <Row className='mt-2'>
        <Col lg={12}>
          <div
            className='d-grid align-items-end'
            style={{ gridTemplateColumns: '1fr auto auto' }}
          >
            <div className='flex-grow-1'>
              <ActivityWithActions
                {...{ actions, onClickAction: handleActionClick }}
              >
                <Unifree
                  label={t('activity-document-file.labels.document-id')}
                  disabled={true}
                  value={values?.uuid || ''}
                  required={required}
                  placeholder={t('activity-document-file.placeholders.uuid')}
                />
              </ActivityWithActions>
            </div>

            {!values?.url ? (
              <FileUploader
                onChange={handleLocalFileUpload}
                accept={accept}
                maxSizeInBytes={maxSizeInBytes}
              />
            ) : (
              <Button
                variant='secondary'
                onClick={handleClearData}
                style={{ width: '40px', marginLeft: '4px' }}
                data-testid='clear-button'
              >
                <Icon iconName='Cleaner' icSize='semiSmall' />
              </Button>
            )}
          </div>
        </Col>
      </Row>
      <Row className='mt-2'>
        <Col lg={12}>
          <Unifree
            label={t('activity-document-file.labels.status')}
            disabled={true}
            value={
              values?.statusDes || t('activity-document-file.status-to-insert')
            }
            required={required}
          />
        </Col>
      </Row>
      <Row className='mt-2'>
        <Col md={6}>
          <Unifree
            label={t('activity-document-file.labels.content-type')}
            disabled={true}
            value={values?.contentType || ''}
            required={required}
            placeholder={getContentTypePlaceholder(accept)}
          />
        </Col>
        <Col md={6}>
          <Unifree
            label={t('activity-document-file.labels.size')}
            disabled={true}
            value={values?.size ? formatFileSize(Number(values.size)) : ''}
            required={required}
            placeholder={t('activity-document-file.placeholders.size')}
          />
        </Col>
      </Row>
      <Row className='mt-2'>
        <Col lg={12}>
          <Unifree
            label={t('activity-document-file.labels.file-name')}
            disabled={true}
            value={values?.originalFilename || ''}
            required={required}
            placeholder={t('activity-document-file.placeholders.file-name')}
          />
        </Col>
      </Row>
    </>
  )
}

export default ActivityDocumentFile
